import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import './InviteMemberModal.css';

function InviteMemberModal({ organizationId, organizationName, onClose, onInviteSent }) {
  const auth = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [inviteList, setInviteList] = useState([]); // Array of {user, isAdmin}
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [successCount, setSuccessCount] = useState(0);
  const [failedInvites, setFailedInvites] = useState([]);
  const searchDebounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setError(null);

    searchDebounceRef.current = setTimeout(async () => {
      try {
        if (auth.user?.id_token) {
          const data = await apiService.searchUsers(auth.user.id_token, searchQuery.trim(), 10);
          // Filter out organizations from results (include individuals and developers)
          // Also filter out users already in the invite list
          const individuals = (data.results || []).filter(user =>
            (user.user_type === 'individual' || user.user_type === 'developer') &&
            !inviteList.some(invite => invite.user.user_id === user.user_id)
          );
          setSearchResults(individuals);
        }
      } catch (err) {
        console.error('Search error:', err);
        setError('Failed to search users');
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery, auth.user, inviteList]);

  const handleAddUser = (user, isAdmin = false) => {
    // Check if user is already in the invite list
    if (inviteList.some(invite => invite.user.user_id === user.user_id)) {
      return;
    }

    setInviteList(prev => [...prev, { user, isAdmin }]);
    setSearchQuery('');
    setSearchResults([]);
    setError(null);
  };

  const handleRemoveUser = (userId) => {
    setInviteList(prev => prev.filter(invite => invite.user.user_id !== userId));
  };

  const handleToggleAdmin = (userId) => {
    setInviteList(prev => prev.map(invite =>
      invite.user.user_id === userId
        ? { ...invite, isAdmin: !invite.isAdmin }
        : invite
    ));
  };

  const handleSendInvites = async () => {
    if (inviteList.length === 0) return;

    setSending(true);
    setError(null);
    setSuccessCount(0);
    setFailedInvites([]);

    const failed = [];
    let success = 0;

    try {
      const idToken = auth.user.id_token;

      // Send invites sequentially to avoid overwhelming the server
      for (const invite of inviteList) {
        try {
          await apiService.inviteOrganizationMember(
            idToken,
            organizationId,
            invite.user.user_id,
            invite.isAdmin
          );
          success++;
          setSuccessCount(success);
        } catch (err) {
          console.error(`Error inviting ${invite.user.display_name}:`, err);
          failed.push({
            user: invite.user,
            error: err.message
          });
        }
      }

      setFailedInvites(failed);

      // If all invites succeeded, close modal
      if (failed.length === 0) {
        if (onInviteSent) {
          onInviteSent();
        }
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        // Some failed, show error
        setError(`${success} invitation(s) sent successfully. ${failed.length} failed.`);
        // Remove successful invites from the list
        setInviteList(prev => prev.filter(invite =>
          failed.some(f => f.user.user_id === invite.user.user_id)
        ));

        if (onInviteSent && success > 0) {
          onInviteSent();
        }
      }
    } catch (err) {
      console.error('Error sending invites:', err);
      setError('An unexpected error occurred');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content invite-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Invite Members to {organizationName}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {successCount > 0 && failedInvites.length === 0 && (
            <div className="success-message">
              Successfully sent {successCount} invitation{successCount !== 1 ? 's' : ''}!
            </div>
          )}

          {/* Search Section */}
          <div className="search-section">
            <label htmlFor="user-search">Search for users to invite:</label>
            <div className="search-input-wrapper">
              <input
                id="user-search"
                type="text"
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="modal-search-input"
                autoFocus
                disabled={sending}
              />
              {isSearching && <div className="search-spinner">Searching...</div>}
            </div>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="search-results">
              <p className="results-label">Click to add to invite list:</p>
              <ul className="results-list">
                {searchResults.map((user) => (
                  <li
                    key={user.user_id}
                    className="result-item"
                    onClick={() => handleAddUser(user, false)}
                  >
                    <div className="result-avatar">
                      {user.display_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="result-info">
                      <div className="result-name">{user.display_name}</div>
                      <div className="result-email">{user.email}</div>
                    </div>
                    <button className="add-user-btn" type="button">
                      + Add
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {searchQuery.trim().length >= 2 && searchResults.length === 0 && !isSearching && (
            <div className="no-results">
              No users found matching "{searchQuery}"
            </div>
          )}

          {/* Invite List */}
          {inviteList.length > 0 && (
            <div className="invite-list-section">
              <div className="invite-list-header">
                <p className="section-label">
                  Users to invite ({inviteList.length})
                </p>
                <button
                  className="clear-all-btn"
                  onClick={() => setInviteList([])}
                  disabled={sending}
                  type="button"
                >
                  Clear All
                </button>
              </div>
              <div className="invite-list">
                {inviteList.map((invite) => (
                  <div key={invite.user.user_id} className="invite-list-item">
                    <div className="invite-user-info">
                      <div className="invite-user-avatar">
                        {invite.user.display_name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div className="invite-user-details">
                        <div className="invite-user-name">{invite.user.display_name}</div>
                        <div className="invite-user-email">{invite.user.email}</div>
                      </div>
                    </div>
                    <div className="invite-controls">
                      <label className="mini-checkbox-label">
                        <input
                          type="checkbox"
                          checked={invite.isAdmin}
                          onChange={() => handleToggleAdmin(invite.user.user_id)}
                          disabled={sending}
                        />
                        <span>Admin</span>
                      </label>
                      <button
                        className="remove-invite-btn"
                        onClick={() => handleRemoveUser(invite.user.user_id)}
                        disabled={sending}
                        type="button"
                        title="Remove from invite list"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <p className="admin-help-text-bottom">
                Admins can invite members, approve coin requests, and manage the organization.
              </p>
            </div>
          )}

          {inviteList.length === 0 && searchQuery.trim().length < 2 && (
            <div className="empty-invite-state">
              <p>Search for users above to add them to your invite list.</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button
            className="send-invite-btn"
            onClick={handleSendInvites}
            disabled={inviteList.length === 0 || sending}
          >
            {sending
              ? `Sending... (${successCount}/${inviteList.length})`
              : `Send ${inviteList.length} Invitation${inviteList.length !== 1 ? 's' : ''}`
            }
          </button>
        </div>
      </div>
    </div>
  );
}

export default InviteMemberModal;
